import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  category: string;
}

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const navigate = useNavigate();

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData: FAQItem[] = [
    // Getting Started
    {
      category: 'Getting Started',
      question: 'What is Score My Offer and how does it work?',
      answer: (
        <>
          <p>Score My Offer is an intelligent platform that helps listing agents efficiently evaluate and compare multiple purchase offers using AI-powered analysis.</p>
          <p><strong>How it works:</strong></p>
          <ul>
            <li>Buyer agents submit offers via email (submit@scoremyoffer.com), shareable links, or the web portal</li>
            <li>Our AI automatically extracts data from Purchase Agreements and Pre-Approval Letters</li>
            <li>Each offer receives a PTC (Probability to Close) score based on financial strength and transaction risk</li>
            <li>Listing agents can view, compare, and analyze all offers in one centralized dashboard</li>
          </ul>
        </>
      )
    },
    {
      category: 'Getting Started',
      question: 'Do I need to create an account?',
      answer: 'Yes, an account is required to use Score My Offer. Listing agents need an account to receive and manage offers. Buyer agents need an account to submit offers via shareable links or the web portal. Email submissions (to submit@scoremyoffer.com) require that the sender\'s email is registered in our system.'
    },
    {
      category: 'Getting Started',
      question: 'What information do I need to sign up?',
      answer: 'To create an account, you\'ll need: your first and last name, email address, password, phone number, and NMLS number (if applicable). Additional profile information can be added later.'
    },

    // Submitting Offers
    {
      category: 'Submitting Offers',
      question: 'What are the different ways to submit an offer?',
      answer: (
        <>
          <p>There are three ways to submit offers:</p>
          <ul>
            <li><strong>📧 Email Submission:</strong> Send PDFs to submit@scoremyoffer.com from your registered email address</li>
            <li><strong>🔗 Shareable Link:</strong> Use a unique link provided by the listing agent to submit offers for a specific property</li>
            <li><strong>📋 Web Portal:</strong> Log in and submit offers directly through your dashboard</li>
          </ul>
          <p>All three methods automatically trigger AI processing and PTC score calculation.</p>
        </>
      )
    },
    {
      category: 'Submitting Offers',
      question: 'What documents are required to submit an offer?',
      answer: (
        <>
          <p><strong>Required documents:</strong></p>
          <ul>
            <li><strong>Purchase Agreement</strong> - Must include property details, purchase price, and terms</li>
            <li><strong>Pre-Approval Letter</strong> - Must include buyer information, lender details, and loan amount</li>
          </ul>
          <p><strong>File requirements:</strong></p>
          <ul>
            <li>PDF format only</li>
            <li>Maximum 10MB per file</li>
            <li>Can be submitted as separate files or one combined PDF</li>
          </ul>
        </>
      )
    },
    {
      category: 'Submitting Offers',
      question: 'Can I submit both documents in one PDF?',
      answer: 'Yes! You can submit a combined PDF containing both the Purchase Agreement and Pre-Approval Letter. Our AI will automatically detect and extract data from both documents. You can also submit them as separate PDF files.'
    },
    {
      category: 'Submitting Offers',
      question: 'How do I use a shareable link to submit an offer?',
      answer: (
        <>
          <p>If a listing agent has shared a submission link with you:</p>
          <ol>
            <li>Click the link or paste it in your browser</li>
            <li>Log in to your account (or sign up if you don\'t have one)</li>
            <li>You\'ll automatically be redirected to the submission page</li>
            <li>Upload your Purchase Agreement and Pre-Approval Letter (PDF files)</li>
            <li>Enter buyer information (name, email, phone)</li>
            <li>Click "Submit Offer"</li>
          </ol>
          <p>You\'ll see your submission in the "My Submissions" page after submitting.</p>
        </>
      )
    },
    {
      category: 'Submitting Offers',
      question: 'What happens after I submit an offer via email?',
      answer: (
        <>
          <p>When you email your offer to submit@scoremyoffer.com:</p>
          <ol>
            <li>Our system receives and validates your email attachments</li>
            <li>PDF files are extracted and uploaded to secure storage</li>
            <li>AI automatically processes the documents and extracts key data</li>
            <li>A PTC score is calculated based on the offer details</li>
            <li>You\'ll receive a confirmation email with the submission details</li>
            <li>The listing agent can immediately view your offer in their dashboard</li>
          </ol>
          <p><strong>Note:</strong> Your registered email address must match the sender email.</p>
        </>
      )
    },

    // Receiving Offers
    {
      category: 'Receiving Offers',
      question: 'How do I create a shareable link for my listing?',
      answer: (
        <>
          <p>As a listing agent, you can generate shareable links:</p>
          <ol>
            <li>Go to the "Listings" page</li>
            <li>Find the property you want to create a link for</li>
            <li>Click "Generate Shareable Link"</li>
            <li>The link is automatically copied to your clipboard</li>
            <li>Share this link with buyer agents via email, text, or any messaging platform</li>
          </ol>
          <p>Each link is unique to the property and can be activated/deactivated at any time.</p>
        </>
      )
    },
    {
      category: 'Receiving Offers',
      question: 'Can I deactivate a shareable link?',
      answer: 'Yes! You can deactivate a shareable link at any time from your Listings page. Click "Deactivate" to prevent new submissions. The link can be reactivated later if needed. Deactivating a link does not delete previously submitted offers.'
    },
    {
      category: 'Receiving Offers',
      question: 'How do I view offers submitted for my listings?',
      answer: (
        <>
          <p>View your offers in two places:</p>
          <ul>
            <li><strong>Listings Page:</strong> Expand any listing to see all submitted offers with key details and scores</li>
            <li><strong>My Offers Page:</strong> See all offers across all your listings in one centralized view</li>
          </ul>
          <p>Click "View Details" on any offer to see the complete information, documents, and PTC score breakdown.</p>
        </>
      )
    },
    {
      category: 'Receiving Offers',
      question: 'Can I see who submitted each offer?',
      answer: 'Yes. For each offer, you can see the buyer agent\'s name, email, and NMLS number (if provided). However, buyer information (the actual home buyer\'s personal details) is kept private and not displayed to protect privacy.'
    },

    // PTC Score
    {
      category: 'PTC Score',
      question: 'What is a PTC Score?',
      answer: (
        <>
          <p>PTC stands for <strong>Probability to Close</strong>. It\'s a score from 0-100 that indicates how likely an offer is to successfully close based on multiple factors.</p>
          <p><strong>Score ranges:</strong></p>
          <ul>
            <li><strong>70-100 (Green):</strong> Excellent - Highly competitive offer</li>
            <li><strong>50-69 (Blue):</strong> Good - Competitive position</li>
            <li><strong>30-49 (Yellow):</strong> Fair - Needs improvement</li>
            <li><strong>0-29 (Red):</strong> Poor - Significant improvements needed</li>
          </ul>
        </>
      )
    },
    {
      category: 'PTC Score',
      question: 'What factors determine the PTC Score?',
      answer: (
        <>
          <p>The PTC Score uses our proprietary algorithm that analyzes multiple aspects of each offer to determine the probability of successful closing. The algorithm considers:</p>
          <ul>
            <li><strong>Financial Strength:</strong> Buyer's financial qualifications and ability to close</li>
            <li><strong>Financing Quality:</strong> Lender credibility and pre-approval verification</li>
            <li><strong>Offer Terms:</strong> Contingencies, timelines, and other contractual elements</li>
            <li><strong>Transaction Risk:</strong> Factors that could impact closing probability</li>
            <li><strong>Professional Vetting:</strong> Verification of involved parties (lenders, agents)</li>
          </ul>
          <p>The exact weighting and calculation methodology is proprietary to ensure the integrity and competitive advantage of our scoring system.</p>
        </>
      )
    },
    {
      category: 'PTC Score',
      question: 'Is the PTC Score always accurate?',
      answer: 'The PTC Score is a suggestion based on specific criteria extracted from submitted documents. It should be used as a guideline, not the sole decision factor. The information displayed is automatically extracted and may not include all details from the purchase agreement. Always verify critical information from the actual documents before making decisions.'
    },
    {
      category: 'PTC Score',
      question: 'Can I see a breakdown of how the score was calculated?',
      answer: 'The Offer Details page shows the final PTC score and interpretation. While we don\'t disclose the exact calculation formula (to protect our proprietary methodology), you can see recommendations for improving the offer score, which provide insights into the key factors that influence the overall rating.'
    },

    // Documents & Data
    {
      category: 'Documents & Data',
      question: 'How accurate is the AI data extraction?',
      answer: (
        <>
          <p>Our AI uses Google Gemini technology to extract data from purchase agreements and pre-approval letters with high accuracy. However:</p>
          <ul>
            <li>The quality of extraction depends on document clarity and format</li>
            <li>Scanned/image-based PDFs may require OCR processing</li>
            <li>Complex or non-standard document formats may have incomplete extraction</li>
            <li>Always verify critical details from the original documents</li>
          </ul>
          <p>A disclaimer on every Offer Details page reminds you to verify information from actual documents.</p>
        </>
      )
    },
    {
      category: 'Documents & Data',
      question: 'Can I download the submitted PDFs?',
      answer: 'Yes. On the Offer Details page, each submitted document has a "⬇ Download" button that allows you to download and review the original PDF files.'
    },
    {
      category: 'Documents & Data',
      question: 'What information is extracted from the Purchase Agreement?',
      answer: (
        <>
          <p>From the Purchase Agreement, we extract:</p>
          <ul>
            <li>Property address and details</li>
            <li>Purchase price and terms</li>
            <li>Earnest money deposit amount</li>
            <li>Contingencies (appraisal, financing, inspection, etc.)</li>
            <li>Closing date and timeline</li>
            <li>Escalation clause (if present)</li>
            <li>Buyer and seller information</li>
            <li>Buyer agent and listing agent details</li>
            <li>Title and escrow information</li>
          </ul>
        </>
      )
    },
    {
      category: 'Documents & Data',
      question: 'What information is extracted from the Pre-Approval Letter?',
      answer: (
        <>
          <p>From the Pre-Approval Letter, we extract:</p>
          <ul>
            <li>Borrower name and details</li>
            <li>Lender name and NMLS number</li>
            <li>Loan officer name and NMLS number</li>
            <li>Mortgage company information</li>
            <li>Pre-approved loan amount</li>
            <li>Loan type (Conventional, FHA, VA, etc.)</li>
            <li>Interest rate (if locked)</li>
            <li>Approval and expiration dates</li>
          </ul>
        </>
      )
    },
    {
      category: 'Documents & Data',
      question: 'What if a document is missing information?',
      answer: 'If critical information cannot be extracted (e.g., missing purchase price or lender NMLS), the offer will still be created but may have incomplete data fields. The PTC score will adjust accordingly. You\'ll see "N/A" or empty fields for missing data, and you should review the original documents for complete information.'
    },

    // Lender Management
    {
      category: 'Lender Management',
      question: 'What is the Lender Verification feature?',
      answer: (
        <>
          <p>The Lender Verification feature helps you assess the credibility of lenders associated with submitted offers:</p>
          <ul>
            <li><strong>Verified Lenders:</strong> Lenders with confirmed NMLS numbers and ratings</li>
            <li><strong>Experience Score:</strong> Based on past transaction history and performance</li>
            <li><strong>Rating System:</strong> 1-5 stars based on closing success rate and reliability</li>
            <li><strong>Transaction Count:</strong> Number of successfully closed deals</li>
          </ul>
          <p>Verified lenders contribute to higher PTC scores.</p>
        </>
      )
    },
    {
      category: 'Lender Management',
      question: 'Can I add notes about lenders or agents?',
      answer: 'Yes! On any Offer Details page, click the "📝 Add Notes" button next to a buyer agent\'s name. You can rate the agent (1-5 stars) and add private notes about your experience working with them. These notes are only visible to you and help track agent performance over time.'
    },

    // My Submissions
    {
      category: 'My Submissions',
      question: 'How can I track offers I\'ve submitted as a buyer agent?',
      answer: 'Go to the "My Submissions" page to see all offers you\'ve submitted via shareable links or email. You can view submission status, property details, submission date, and files count. Click "View Details" to see the full offer information and PTC score (if calculated).'
    },
    {
      category: 'My Submissions',
      question: 'Will I be notified when my offer is reviewed?',
      answer: 'Currently, the system sends email confirmations when your offer is successfully processed. Real-time notifications for when listing agents view or act on your offer are planned for a future release.'
    },

    // Privacy & Security
    {
      category: 'Privacy & Security',
      question: 'Is my data secure?',
      answer: 'Yes. All data is stored securely in Google Firebase with enterprise-grade security. Documents are uploaded to secure cloud storage, and access is strictly controlled through authentication. Only authorized users (listing agents and the submitting buyer agent) can access offer documents and information.'
    },
    {
      category: 'Privacy & Security',
      question: 'Can listing agents see buyer personal information?',
      answer: 'No. Buyer personal information (home buyer\'s name, contact details, financial information) is kept private. Listing agents can only see the buyer agent\'s information (name, email, NMLS) and offer details extracted from documents. This protects buyer privacy while providing necessary transaction information.'
    },
    {
      category: 'Privacy & Security',
      question: 'Who can access submitted offers?',
      answer: 'Only the listing agent who owns the property and the buyer agent who submitted the offer can access the full offer details and documents. No other users can view your submissions or documents.'
    },

    // Troubleshooting
    {
      category: 'Troubleshooting',
      question: 'My email submission didn\'t go through. What should I check?',
      answer: (
        <>
          <p>If your email submission failed, verify:</p>
          <ul>
            <li>You sent the email from your registered email address</li>
            <li>The email was sent to <strong>submit@scoremyoffer.com</strong> (check spelling)</li>
            <li>PDF attachments are included and under 10MB each</li>
            <li>Both Purchase Agreement and Pre-Approval Letter are attached (or combined in one PDF)</li>
            <li>Your account is active and email is verified</li>
          </ul>
          <p>You should receive a confirmation email within 5 minutes. Check your spam folder if you don\'t see it.</p>
        </>
      )
    },
    {
      category: 'Troubleshooting',
      question: 'The shareable link says "invalid or deactivated". What does this mean?',
      answer: 'This means either the link was mistyped, or the listing agent has deactivated the link. Contact the listing agent to verify the link is still active and request a new link if needed. Links can be deactivated if the property is sold, under contract, or if the listing agent wants to pause submissions temporarily.'
    },
    {
      category: 'Troubleshooting',
      question: 'The extracted data is incorrect or missing. What should I do?',
      answer: (
        <>
          <p>If extracted data is incorrect:</p>
          <ul>
            <li>Always download and review the original PDF documents</li>
            <li>The disclaimer at the bottom of each Offer Details page reminds you to verify from source documents</li>
            <li>Contact the buyer agent if critical information is missing or unclear</li>
            <li>Complex or non-standard document formats may have extraction issues</li>
          </ul>
          <p>The PTC score is recalculated based on available extracted data, so missing information may affect the score.</p>
        </>
      )
    },
    {
      category: 'Troubleshooting',
      question: 'I forgot my password. How do I reset it?',
      answer: 'On the login page, click "Forgot Password?" and enter your registered email address. You\'ll receive a password reset link via email. Click the link and follow the instructions to create a new password.'
    },

    // Features
    {
      category: 'Features',
      question: 'Can I compare multiple offers side-by-side?',
      answer: 'Yes! On the "My Offers" page, use the filters to view offers by property, agent, lender, or date. The table view shows key details and PTC scores for easy comparison. You can sort by score, date, or property to identify the strongest offers quickly.'
    },
    {
      category: 'Features',
      question: 'Can I filter and search my offers?',
      answer: 'Yes. Both the "Listings" and "My Offers" pages include filtering options. You can filter by date range, property address, buyer agent, or lender. Pagination helps you navigate through large numbers of offers efficiently.'
    },
    {
      category: 'Features',
      question: 'What happens if I submit a duplicate offer?',
      answer: 'The system is designed to handle duplicate submissions. If you submit the same offer multiple times (with updated documents), the system will create separate submission records. Each submission will have its own PTC score based on the documents provided at that time.'
    }
  ];

  const categories = ['all', ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory);

  return (
    <div className="faq-container">
      <div className="faq-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="faq-title-section">
          <h1>Frequently Asked Questions</h1>
          <p className="faq-subtitle">Find answers to common questions about Score My Offer</p>
        </div>
      </div>

      <div className="faq-categories">
        {categories.map(category => (
          <button
            key={category}
            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
            onClick={() => {
              setActiveCategory(category);
              setActiveIndex(null);
            }}
          >
            {category === 'all' ? 'All Questions' : category}
          </button>
        ))}
      </div>

      <div className="faq-content">
        {filteredFAQs.map((item, index) => (
          <div key={index} className="faq-item">
            <button
              className={`faq-question ${activeIndex === index ? 'active' : ''}`}
              onClick={() => toggleAccordion(index)}
            >
              <span className="question-text">{item.question}</span>
              <span className="toggle-icon">{activeIndex === index ? '−' : '+'}</span>
            </button>
            {activeIndex === index && (
              <div className="faq-answer">
                {typeof item.answer === 'string' ? <p>{item.answer}</p> : item.answer}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="faq-footer">
        <h3>Still have questions?</h3>
        <p>Contact us at <a href="mailto:support@scoremyoffer.com">support@scoremyoffer.com</a></p>
      </div>
    </div>
  );
};

export default FAQ;
